import {
  type CreateStoryConfigRevision,
  StoryConfigRevision,
} from "@/api/story-configs-types";
import { RoleName } from "@/api/users-types";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { useHasRole } from "@/hooks/use-has-role";
import { useCreateStoryConfigRevision } from "@/queries/story-configs/use-create-story-config-revision";
import { useStoryConfigById } from "@/queries/story-configs/use-story-config-by-id";
import { getLatest } from "@/utils/get-latest";
import { Add, DeleteOutline, InfoOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { useState } from "react";
import { useNavigate } from "react-router";

export function CreateStoryConfigRevision({
  disabled,
  configId,
}: {
  disabled?: boolean;
  configId: string;
}) {
  const [open, setOpen] = useState(false);

  const canCreate = useHasRole(RoleName.promptEngineer);

  if (!canCreate) {
    return null;
  }

  return (
    <>
      <CreateRevisionButton
        disabled={!!disabled}
        onClick={() => setOpen(true)}
      />
      <CreateRevisionDialog
        open={open}
        configId={configId}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

function CreateRevisionButton({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip
      title={
        !disabled ? "" : "You can only edit the latest revision of story config"
      }
      placement="bottom"
      disableInteractive
    >
      <Box>
        <Button variant="contained" disabled={disabled} onClick={onClick}>
          New revision
        </Button>
      </Box>
    </Tooltip>
  );
}

function CreateRevisionDialog({
  open,
  configId,
  onClose,
}: {
  open: boolean;
  configId: string;
  onClose: () => void;
}) {
  const { data } = useStoryConfigById(configId);

  const navigate = useNavigate();

  const {
    mutate: createRevision,
    isPending,
    error,
  } = useCreateStoryConfigRevision();

  const latestRevision = getLatest(data!.revisions); // we can assume data is defined in query cache, because the dialog can only be opened when the data is already fetched

  const form = useForm({
    defaultValues: toFormValues(latestRevision),
    onSubmit: ({ value, formApi }) => {
      if (formApi.state.isDefaultValue) {
        handleClose();
        return;
      }
      const newRevision = fromFormValues(value);
      createRevision(
        { configId, data: newRevision },
        {
          onSuccess: () => {
            handleClose();
            navigate(`/story-configs/${configId}`);
          },
        },
      );
    },
  });

  function handleClose() {
    form.reset();
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form>
        <DialogTitle>Create New Story Config Revision</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={error ? 0 : 0.75}>
            {error && <ApiErrorAlert error={error} />}
            <form.Field
              name="revisionNote"
              children={(field) => (
                <TextField
                  label="Revision note"
                  value={field.state.value}
                  onChange={(e) => {
                    field.handleChange(e.currentTarget.value);
                  }}
                  onBlur={field.handleBlur}
                />
              )}
            />
            <form.Field
              name="storyName"
              validators={{
                onSubmit: ({ value }) => {
                  if (!value) {
                    return "Story name is required";
                  }
                },
              }}
              children={(field) => (
                <TextField
                  label="Story name"
                  value={field.state.value}
                  error={!field.state.meta.isValid}
                  helperText={field.state.meta.errors[0]}
                  onChange={(e) => {
                    field.handleChange(e.currentTarget.value);
                  }}
                  onBlur={field.handleBlur}
                />
              )}
            />
            <Stack
              direction={"row"}
              gap={2}
              alignItems={"center"}
              sx={{ pb: 1 }}
            >
              <Typography variant="subtitle1" fontWeight={"bold"}>
                Characters
              </Typography>
              <Tooltip
                title={
                  <Stack gap={1}>
                    <Typography fontWeight={"bold"}>Characters</Typography>
                    <Typography>
                      {
                        "Additional Characters: Variables can be added for version specific characters"
                      }
                    </Typography>
                    <Typography>
                      {
                        "Randomized Characters: Variables will randomly select a character, given multiple character values"
                      }
                    </Typography>
                  </Stack>
                }
                placement="top"
              >
                <InfoOutlined />
              </Tooltip>
            </Stack>
            <form.Field
              name="characters"
              mode={"array"}
              children={(field) => {
                return (
                  <>
                    {field.state.value.map((_, index) => {
                      return (
                        <Stack
                          key={index}
                          direction="row"
                          gap={2}
                          alignItems={"flex-start"}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              height: 53.25,
                              alignItems: "center",
                            }}
                          >
                            <ButtonBase
                              sx={{
                                width: 32,
                                height: 32,
                              }}
                              onClick={() => {
                                field.removeValue(index);
                              }}
                            >
                              <DeleteOutline />
                            </ButtonBase>
                          </Box>
                          <form.Field
                            name={`characters[${index}].variableName`}
                            validators={{
                              onSubmit: ({ value }) => {
                                if (!value) {
                                  return "Variable name is required";
                                }
                              },
                            }}
                            children={(field_variableName) => (
                              <TextField
                                error={!field_variableName.state.meta.isValid}
                                helperText={
                                  field_variableName.state.meta.errors[0]
                                }
                                label="Variable name"
                                value={field_variableName.state.value}
                                onChange={(e) => {
                                  field_variableName.handleChange(
                                    e.currentTarget.value,
                                  );
                                }}
                                onBlur={field_variableName.handleBlur}
                              />
                            )}
                          />
                          <form.Field
                            name={`characters[${index}].characters`}
                            mode="array"
                            children={(field_characters) => {
                              return (
                                <Stack
                                  direction="column"
                                  gap={1}
                                  sx={{ flexGrow: 1 }}
                                >
                                  {field_characters.state.value.map(
                                    (_, charIndex) => (
                                      <Stack
                                        key={charIndex}
                                        direction="row"
                                        gap={1}
                                        alignItems={"center"}
                                      >
                                        <ButtonBase
                                          sx={{ width: 32, height: 32 }}
                                          onClick={() => {
                                            if (
                                              field_characters.state.value
                                                .length === 1
                                            ) {
                                              // if it's the last character, set the value to empty string instead of removing it
                                              field_characters.setValue([""]);
                                            } else {
                                              field_characters.removeValue(
                                                charIndex,
                                              );
                                            }
                                          }}
                                        >
                                          <DeleteOutline />
                                        </ButtonBase>
                                        <form.Field
                                          name={`characters[${index}].characters[${charIndex}]`}
                                          validators={{
                                            onSubmit: ({ value }) => {
                                              if (!value) {
                                                return "Character is required";
                                              }
                                            },
                                          }}
                                          children={(field_character) => (
                                            <TextField
                                              sx={{ flexGrow: 1 }}
                                              label={`Character ${charIndex + 1}`}
                                              value={
                                                field_character.state.value
                                              }
                                              error={
                                                !field_character.state.meta
                                                  .isValid
                                              }
                                              helperText={
                                                field_character.state.meta
                                                  .errors[0]
                                              }
                                              onChange={(e) => {
                                                field_character.handleChange(
                                                  e.currentTarget.value,
                                                );
                                              }}
                                              onBlur={
                                                field_character.handleBlur
                                              }
                                            />
                                          )}
                                        />
                                      </Stack>
                                    ),
                                  )}
                                  <ButtonBase
                                    sx={{ width: 32, height: 32 }}
                                    onClick={() => {
                                      field_characters.pushValue("");
                                    }}
                                  >
                                    <Add />
                                  </ButtonBase>
                                </Stack>
                              );
                            }}
                          />
                        </Stack>
                      );
                    })}
                    <ButtonBase
                      sx={{ width: 32, height: 32 }}
                      onClick={() => {
                        field.pushValue({
                          variableName: "",
                          characters: [""],
                        });
                      }}
                    >
                      <Add />
                    </ButtonBase>
                  </>
                );
              }}
            />
            <Stack
              direction={"row"}
              gap={2}
              alignItems={"center"}
              sx={{ pb: 1 }}
            >
              <Typography variant="subtitle1" fontWeight={"bold"}>
                Character image styles
              </Typography>
              <Tooltip
                title={
                  <Stack gap={1}>
                    <Typography fontWeight={"bold"}>
                      Character Image Style
                    </Typography>
                    <Typography>
                      {
                        "Character image style variables defined as {Character Variable Name_image_style}"
                      }
                    </Typography>
                  </Stack>
                }
                placement="top"
              >
                <InfoOutlined />
              </Tooltip>
            </Stack>
            <form.Field
              name="boyCharacterImageStyle"
              validators={{
                onSubmit: ({ value }) => {
                  if (!value) {
                    return "Boy character image style is required";
                  }
                },
              }}
              children={(field) => (
                <TextField
                  error={!field.state.meta.isValid}
                  helperText={field.state.meta.errors[0]}
                  label="Boy character image style"
                  value={field.state.value}
                  onChange={(e) => {
                    field.handleChange(e.currentTarget.value);
                  }}
                  onBlur={field.handleBlur}
                />
              )}
            />
            <form.Field
              name="girlCharacterImageStyle"
              validators={{
                onSubmit: ({ value }) => {
                  if (!value) {
                    return "Girl character image style is required";
                  }
                },
              }}
              children={(field) => (
                <TextField
                  error={!field.state.meta.isValid}
                  helperText={field.state.meta.errors[0]}
                  label="Girl character image style"
                  value={field.state.value}
                  onChange={(e) => {
                    field.handleChange(e.currentTarget.value);
                  }}
                  onBlur={field.handleBlur}
                />
              )}
            />
            <form.Field
              name="characterImageStyles"
              mode={"array"}
              children={(field) => (
                <>
                  {field.state.value.map((_, index) => {
                    return (
                      <Stack
                        key={index}
                        direction="row"
                        gap={2}
                        alignItems={"center"}
                      >
                        <ButtonBase
                          sx={{ width: 32, height: 32 }}
                          onClick={() => {
                            field.removeValue(index);
                          }}
                        >
                          <DeleteOutline />
                        </ButtonBase>
                        <form.Field
                          name={`characterImageStyles[${index}].character`}
                          validators={{
                            onSubmit: ({ value }) => {
                              if (!value) {
                                return "Character name is required";
                              }
                            },
                          }}
                          children={(field) => (
                            <TextField
                              error={!field.state.meta.isValid}
                              helperText={field.state.meta.errors[0]}
                              label="Character name"
                              value={field.state.value}
                              onChange={(e) => {
                                field.handleChange(e.currentTarget.value);
                              }}
                              onBlur={field.handleBlur}
                            />
                          )}
                        />
                        <form.Field
                          name={`characterImageStyles[${index}].imageStyle`}
                          validators={{
                            onSubmit: ({ value }) => {
                              if (!value) {
                                return "Character image style is required";
                              }
                            },
                          }}
                          children={(field) => (
                            <TextField
                              sx={{ flexGrow: 1 }}
                              error={!field.state.meta.isValid}
                              helperText={field.state.meta.errors[0]}
                              label="Character image style"
                              value={field.state.value}
                              onChange={(e) => {
                                field.handleChange(e.currentTarget.value);
                              }}
                              onBlur={field.handleBlur}
                            />
                          )}
                        />
                      </Stack>
                    );
                  })}
                  <ButtonBase
                    sx={{ width: 32, height: 32 }}
                    onClick={() => {
                      field.pushValue({ character: "", imageStyle: "" });
                    }}
                  >
                    <Add />
                  </ButtonBase>
                </>
              )}
            />
            <form.Field
              name="environmentDescription"
              validators={{
                onSubmit: ({ value }) => {
                  if (!value) {
                    return "Environment description is required";
                  }
                },
              }}
              children={(field) => (
                <TextField
                  error={!field.state.meta.isValid}
                  helperText={field.state.meta.errors[0]}
                  label="Environment description"
                  value={field.state.value}
                  onChange={(e) => {
                    field.handleChange(e.currentTarget.value);
                  }}
                  onBlur={field.handleBlur}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={form.handleSubmit}
            loading={isPending}
            variant={"contained"}
          >
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

type CreateStoryConfigRevisionFormValues = {
  revisionNote: string;
  storyName: string;
  boyCharacterImageStyle: string;
  girlCharacterImageStyle: string;
  characterImageStyles: { character: string; imageStyle: string }[];
  characters: { variableName: string; characters: string[] }[];
  environmentDescription: string;
};

function toFormValues(
  revision: StoryConfigRevision | undefined,
): CreateStoryConfigRevisionFormValues {
  return {
    revisionNote: "",
    storyName: revision?.storyName ?? "",
    boyCharacterImageStyle: revision?.boyCharacterImageStyle ?? "",
    girlCharacterImageStyle: revision?.girlCharacterImageStyle ?? "",
    characterImageStyles: Object.entries(
      revision?.characterImageStyles ?? {},
    ).map(([character, imageStyle]) => ({ character, imageStyle })),
    characters: (revision?.characters ?? []).map(
      ([variableName, characters]) => ({
        variableName,
        characters,
      }),
    ),
    environmentDescription: revision?.environmentDescription ?? "",
  };
}

function fromFormValues(
  values: CreateStoryConfigRevisionFormValues,
): CreateStoryConfigRevision {
  return {
    storyName: values.storyName,
    boyCharacterImageStyle: values.boyCharacterImageStyle,
    girlCharacterImageStyle: values.girlCharacterImageStyle,
    characterImageStyles: values.characterImageStyles.reduce(
      (acc, { character, imageStyle }) => {
        acc[character] = imageStyle;
        return acc;
      },
      {} as Record<string, string>,
    ),
    characters: values.characters.map(({ variableName, characters }) => [
      variableName,
      characters,
    ]),
    environmentDescription: values.environmentDescription,
    revisionNote: values.revisionNote,
  };
}
